const REDEMPTION_TYPE_MAP = {
  BANK_ACCOUNT: 'cash',
  WPS: 'savings',
  VOUCHER: 'gift_cards',
};

export function transformSummary(raw) {
  if (!raw) {
    return raw;
  }
  return {
    total_granted_minor: raw.total_granted_minor ?? 0,
    total_redeemed_minor: raw.total_redeemed_minor ?? 0,
    awards_granted: raw.awards_count ?? 0,
    user_participation: Math.round(raw.participation_percent ?? 0),
  };
}

export function transformSpendOverTime(raw) {
  if (!raw || !raw.data) {
    return { labels: [], datasets: [] };
  }

  const periodSet = new Set();
  const divisionData = new Map();

  for (const row of raw.data) {
    periodSet.add(row.period);
    const divName = row.division_name || 'Unassigned';
    if (!divisionData.has(divName)) {
      divisionData.set(divName, new Map());
    }
    const existing = divisionData.get(divName).get(row.period) || 0;
    divisionData.get(divName).set(row.period, existing + row.amount_minor);
  }

  const labels = [...periodSet].sort();

  const datasets = [];
  for (const [divName, periodMap] of divisionData) {
    datasets.push({
      division_name: divName,
      granted_values_minor: labels.map(p => periodMap.get(p) || 0),
    });
  }

  return { labels, datasets };
}

export function transformRedemptionBreakdown(raw) {
  const result = {
    cash: { amount_minor: 0, percentage: 0 },
    savings: { amount_minor: 0, percentage: 0 },
    gift_cards: { amount_minor: 0, percentage: 0 },
  };

  if (!raw || !raw.data) {
    return result;
  }

  for (const row of raw.data) {
    const key = REDEMPTION_TYPE_MAP[row.redemption_type];
    if (key) {
      result[key] = {
        amount_minor: row.amount_minor ?? 0,
        percentage: row.percentage ?? 0,
      };
    }
  }

  return result;
}

export function transformEngagement(raw) {
  if (!raw) {
    return null;
  }

  const extractSeries = series => {
    if (!series) {
      return { total: 0, change: 0, trend: [] };
    }
    return {
      total: series.total ?? 0,
      change: series.percent_change ?? 0,
      trend: (series.daily_values || []).map(dp => dp.value),
    };
  };

  const sent = extractSeries(raw.recognitions_sent);
  const received = extractSeries(raw.recognitions_received);
  const participation = extractSeries(raw.participation_rate);

  return {
    recognition_sent: sent.total,
    recognition_sent_change_pct: sent.change,
    recognition_sent_trend: sent.trend,
    nominations_received: received.total,
    nominations_received_change_pct: received.change,
    nominations_received_trend: received.trend,
    employee_participation: participation.total,
    employee_participation_change_pct: participation.change,
    employee_participation_trend: participation.trend,
  };
}

export function transformTopEmployees(raw) {
  if (!raw || !raw.data) {
    return [];
  }

  return raw.data.map(emp => ({
    employee_id: emp.employee_id,
    full_name: emp.full_name,
    email: emp.work_email ?? '',
    nominations_count: emp.nominations_received ?? 0,
    recognitions_count: emp.recognitions_count ?? 0,
  }));
}

export function transformTopRecognisers(raw) {
  if (!raw || !raw.data) {
    return [];
  }

  return raw.data.map(rec => ({
    employee_id: rec.employee_id,
    full_name: rec.full_name,
    sent_count: rec.nominations_sent ?? 0,
  }));
}

export function transformQualities(raw) {
  if (!raw || !raw.data) {
    return null;
  }

  const qualities = raw.data.map(row => ({
    quality_id: row.value_name,
    quality_name: row.value_name,
    count: row.count ?? 0,
    percentage: row.percentage ?? 0,
    top_employees: [],
  }));

  return { qualities };
}
